import React, {useCallback} from 'react';

import {RoundButton, ShadowCard} from '@optimaxdev/design-system';
import {ActionCreatorWithPayload} from '@reduxjs/toolkit';
import {connect} from 'react-redux';

import {CloudinaryImage} from 'components/cloudinaryImage/cloudinaryImage';
import {
    CODE_FILE_SIZE_ERROR,
    CODE_FILE_TYPE_ERROR,
    CODE_FILE_UNDEFINED_ERROR,
} from 'constants/file/fileCode';
import {FILE_TYPES, MAX_FILE_SIZE} from 'constants/file/fileTypes';
import {ChangeResultType, Uploader} from 'desktop/components/helpers/uploader/uploader';
import {ApplicationStoreType} from 'desktop/store';

import {FieldType, ErrorType, FileSpeciesType} from 'types/successPage';

import {getValidatedItems} from '../../store/selectors/orderItems/orderItems';
import {DoctorDetailsAndUploadValidatedType} from '../../types/orderItemsTypes';

import {SuccessUpload} from './successUpload/successUpload';

import s from './uploadSuccessPageFileRedesign.scss';

const errorTypes = {
    [CODE_FILE_SIZE_ERROR]: {
        message: 'The file size is too big, please upload a file smaller than 5MB',
        title: 'The file size is too big',
    },
    [CODE_FILE_TYPE_ERROR]: {
        message: 'This file type is not supported. Please upload a PDF, document or image file.',
        title: 'File not supported',
    },
    [CODE_FILE_UNDEFINED_ERROR]: {
        message: 'The file was not uploaded, please try again',
        title: 'File was not uploaded',
    },
};

export type PropsType = {
    image: string;
    name: string;
    changeField: ActionCreatorWithPayload<FieldType, string>;
    orderItemId: string;
    error: ErrorType;
    type: string;
    fileSrc: string;
    fileName: string;
    fileType: FileSpeciesType;
    validatedItem?: {upload: string};
    setErrors: ActionCreatorWithPayload<DoctorDetailsAndUploadValidatedType, string>;
};

/**
 * This component has upload file on success page and three states file error, file success and default state
 * @param {PropsType} props - component props
 * @constructor
 */
export const UploadSuccessPageFileRedesign: React.FC<PropsType> = ({
    changeField,
    error,
    fileName,
    fileSrc,
    fileType,
    image,
    name,
    orderItemId,
    setErrors,
    type,
    validatedItem,
}) => {
    /**
     * changeField sets file in store
     * and reset errors in store b/c customer uploads the file by order id
     */
    const handleChange = useCallback(
        (result: ChangeResultType) => {
            if (result.type === 'success')
                changeField({
                    fileName: result.file.name,
                    fileSrc: result.fileSrc,
                    fileType: result.file.type as FileSpeciesType,
                    orderItemId,
                    type: result.type,
                } as FieldType);
            else
                changeField({
                    errorType: result.error_type,
                    fileSrc: '',
                    orderItemId,
                    type: result.type,
                } as FieldType);
            setErrors({
                [orderItemId]: {
                    upload: '',
                },
            });
        },
        [changeField, orderItemId, setErrors],
    );

    const errorMessage =
        type === 'error' && (error === 0 || error)
            ? errorTypes[error].message
            : validatedItem?.upload || '';

    return (
        <ShadowCard className={s.card} corner={5} saturate={2}>
            <div className={s.cardHeader}>
                <p>Add prescription.</p>
            </div>
            <div className={s.cardBody}>
                <div className={s.lensesRow}>
                    <CloudinaryImage alt={name} className={s.image} src={image} />
                    <p className={s.name}>{name}</p>
                </div>
                {type === 'success' ? (
                    <SuccessUpload
                        fileName={fileName}
                        fileSrc={fileSrc}
                        fileType={fileType}
                        handleChange={handleChange}
                        name={name}
                    />
                ) : (
                    <>
                        <div className={s.defaultUpload}>
                            <Uploader
                                fileTypes={FILE_TYPES}
                                handleChange={handleChange}
                                id="uploadFile"
                                maxSize={MAX_FILE_SIZE}
                            >
                                <RoundButton size="extraSmall">Upload Prescription</RoundButton>
                            </Uploader>
                        </div>
                    </>
                )}
                {errorMessage && <p className={s.errorMessage}>*{errorMessage}</p>}
            </div>
        </ShadowCard>
    );
};

/**
 * Map state to props
 * @param {ApplicationStoreType} store - redux store
 * @param {string} orderItemId - order item id
 * @returns {PropsType} props
 */
const mapStateToProps = (store: ApplicationStoreType, {orderItemId}: {orderItemId: string}) => ({
    validatedItem: getValidatedItems(store)[orderItemId] as {upload: string},
});

export const UploadSuccessPageFileRedesignConnected = connect(mapStateToProps)(
    UploadSuccessPageFileRedesign,
);
